// Layered "coverflow" carousel that loops endlessly. Every card sits in one stacked grid cell and is
// positioned by its circular distance from the active card, so stepping past the last card simply moves
// forward to the first. The centred card is full size and on top; neighbours shrink, tuck behind and fade.
// Arrows, swipe/drag, arrow keys and clicking a side card all move the carousel.
class SCoverflow extends HTMLElement {
  #ac;
  #items = [];
  #active = 0;
  #current;
  #stage;
  #startX = null;
  #dragged = false;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.#stage = this.querySelector('[data-coverflow-track]');
    if (!this.#stage) return;
    this.#items = [...this.#stage.querySelectorAll('[data-coverflow-item]')];
    this.#current = this.querySelector('[data-coverflow-current]');
    if (this.#items.length === 0) return;

    this.querySelector('[data-coverflow-prev]')?.addEventListener('click', () => this.#go(this.#active - 1), opts);
    this.querySelector('[data-coverflow-next]')?.addEventListener('click', () => this.#go(this.#active + 1), opts);

    this.#items.forEach((item, index) => {
      item.addEventListener('click', (event) => {
        if (this.#dragged) {
          event.preventDefault();
          return;
        }
        if (index === this.#active) return;
        event.preventDefault();
        this.#go(index);
      }, opts);
    });

    this.#stage.addEventListener('focusin', (event) => {
      const index = this.#items.indexOf(event.target.closest?.('[data-coverflow-item]'));
      if (index > -1 && index !== this.#active) this.#go(index);
    }, opts);

    this.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      const rtl = getComputedStyle(this).direction === 'rtl';
      const step = (event.key === 'ArrowRight') !== rtl ? 1 : -1;
      event.preventDefault();
      this.#go(this.#active + step);
    }, opts);

    this.#stage.addEventListener('pointerdown', (event) => {
      this.#startX = event.clientX;
      this.#dragged = false;
    }, opts);
    this.#stage.addEventListener('pointerup', (event) => {
      if (this.#startX === null) return;
      const delta = event.clientX - this.#startX;
      this.#startX = null;
      if (Math.abs(delta) < 40) return;
      this.#dragged = true;
      this.#go(this.#active + (delta < 0 ? 1 : -1));
      setTimeout(() => { this.#dragged = false; }, 0);
    }, opts);
    this.#stage.addEventListener('pointercancel', () => { this.#startX = null; }, opts);
    this.#stage.addEventListener('dragstart', (event) => event.preventDefault(), opts);
    window.addEventListener('resize', () => this.#render(), opts);

    // Open on the middle colourway so cards fan out on both sides.
    this.#active = Math.floor((this.#items.length - 1) / 2);
    this.#render();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #go(index) {
    const count = this.#items.length;
    this.#active = ((index % count) + count) % count;
    this.#render();
  }

  #render() {
    const count = this.#items.length;
    const rtl = getComputedStyle(this).direction === 'rtl' ? -1 : 1;

    this.#items.forEach((item, index) => {
      // Shortest signed distance around the loop, so the last card sits just left of the first.
      let offset = index - this.#active;
      if (offset > count / 2) offset -= count;
      if (offset < -count / 2) offset += count;
      const distance = Math.abs(offset);
      const visible = distance <= 3;
      const width = item.offsetWidth || 1;
      const shift = Math.sign(offset) * (distance === 0 ? 0 : width * (0.55 + (distance - 1) * 0.38)) * rtl;
      const scale = 1 - Math.min(distance, 3) * 0.15;
      const veil = item.querySelector('[data-coverflow-veil]');

      item.style.transform = `translateX(${shift}px) scale(${scale})`;
      item.style.zIndex = String(50 - distance);
      item.style.opacity = visible ? '1' : '0';
      item.style.pointerEvents = visible ? '' : 'none';
      if (veil) veil.style.opacity = String(Math.min(distance, 3) * 0.2);
      item.toggleAttribute('data-active', distance === 0);
      item.toggleAttribute('inert', !visible);
    });

    if (this.#current) this.#current.textContent = String(this.#active + 1);
  }
}

if (!customElements.get('s-coverflow')) customElements.define('s-coverflow', SCoverflow);
